(() => {
  'use strict';

  const config = window.ReemCurrency || {};
  const base = String(config.base || 'AED').toUpperCase();
  const storageKey = 'reem_manual_display_currency';
  const legacyKey = 'reem_display_currency';

  // Complete practical country-to-currency map. Countries sharing a currency are grouped.
  const countryMap = Object.assign({
    AE:'AED', US:'USD', EC:'USD', SV:'USD', PA:'USD', PR:'USD', TL:'USD',
    FR:'EUR', DE:'EUR', ES:'EUR', IT:'EUR', PT:'EUR', NL:'EUR', BE:'EUR', LU:'EUR', IE:'EUR', AT:'EUR', FI:'EUR', GR:'EUR', CY:'EUR', MT:'EUR', SK:'EUR', SI:'EUR', EE:'EUR', LV:'EUR', LT:'EUR', HR:'EUR', AD:'EUR', MC:'EUR', SM:'EUR', VA:'EUR', ME:'EUR', XK:'EUR',
    GB:'GBP', SA:'SAR', QA:'QAR', KW:'KWD', BH:'BHD', OM:'OMR',
    MA:'MAD', DZ:'DZD', TN:'TND', EG:'EGP', LY:'LYD', JO:'JOD', LB:'LBP', IQ:'IQD', YE:'YER', SY:'SYP', IL:'ILS', PS:'ILS', TR:'TRY', IR:'IRR',
    CA:'CAD', AU:'AUD', NZ:'NZD', JP:'JPY', CN:'CNY', HK:'HKD', MO:'MOP', TW:'TWD', KR:'KRW', IN:'INR', PK:'PKR', BD:'BDT', LK:'LKR', NP:'NPR', BT:'BTN', MV:'MVR',
    SG:'SGD', MY:'MYR', ID:'IDR', TH:'THB', VN:'VND', PH:'PHP', KH:'KHR', LA:'LAK', MM:'MMK', BN:'BND',
    CH:'CHF', LI:'CHF', SE:'SEK', NO:'NOK', DK:'DKK', IS:'ISK', PL:'PLN', CZ:'CZK', HU:'HUF', RO:'RON', BG:'BGN', RS:'RSD', BA:'BAM', MK:'MKD', AL:'ALL', MD:'MDL', UA:'UAH', BY:'BYN', RU:'RUB', GE:'GEL', AM:'AMD', AZ:'AZN', KZ:'KZT', UZ:'UZS', KG:'KGS', TJ:'TJS', TM:'TMT',
    ZA:'ZAR', NG:'NGN', GH:'GHS', KE:'KES', TZ:'TZS', UG:'UGX', ET:'ETB', SD:'SDG', SS:'SSP', SO:'SOS', DJ:'DJF', ER:'ERN', RW:'RWF', BI:'BIF', CD:'CDF', CG:'XAF', CM:'XAF', GA:'XAF', GQ:'XAF', TD:'XAF', CF:'XAF', SN:'XOF', CI:'XOF', ML:'XOF', BF:'XOF', NE:'XOF', TG:'XOF', BJ:'XOF', GW:'XOF', GM:'GMD', GN:'GNF', SL:'SLL', LR:'LRD', MR:'MRU', CV:'CVE', AO:'AOA', ZM:'ZMW', ZW:'ZWL', BW:'BWP', NA:'NAD', SZ:'SZL', LS:'LSL', MW:'MWK', MZ:'MZN', MG:'MGA', MU:'MUR', SC:'SCR', KM:'KMF',
    MX:'MXN', BR:'BRL', AR:'ARS', CL:'CLP', CO:'COP', PE:'PEN', UY:'UYU', PY:'PYG', BO:'BOB', VE:'VES', GY:'GYD', SR:'SRD', CR:'CRC', GT:'GTQ', HN:'HNL', NI:'NIO', BZ:'BZD', DO:'DOP', HT:'HTG', JM:'JMD', TT:'TTD', BS:'BSD', BB:'BBD', BZ:'BZD',
    AF:'AFN', MN:'MNT', FJ:'FJD', PG:'PGK', WS:'WST', TO:'TOP', VU:'VUV', SB:'SBD'
  }, config.countryMap || {});

  const supported = new Set((config.supported || Object.values(countryMap).concat([base])).map(v => String(v).toUpperCase()));
  let autoCurrency = null;
  let detectedCountry = String(config.country || window.Shopify?.country || '').toUpperCase();

  // Remove the old automatic value that caused KWD to stay locked after changing VPN.
  try { localStorage.removeItem(legacyKey); } catch (_) {}

  const currencyForCountry = country => countryMap[String(country || '').toUpperCase()] || base;

  const getCurrency = () => {
    let manual = null;
    try { manual = localStorage.getItem(storageKey); } catch (_) {}
    manual = String(manual || '').toUpperCase();
    if (manual && supported.has(manual)) return manual;
    if (autoCurrency && supported.has(autoCurrency)) return autoCurrency;
    const fallback = currencyForCountry(detectedCountry);
    return supported.has(fallback) ? fallback : base;
  };

  const fractionDigits = currency => ['KWD','BHD','OMR','JOD','TND','LYD','IQD'].includes(currency) ? 3 : 2;
  const format = (amount, currency) => {
    try {
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en-US', {
        style: 'currency', currency, currencyDisplay: 'symbol',
        minimumFractionDigits: fractionDigits(currency),
        maximumFractionDigits: fractionDigits(currency)
      }).format(amount);
    } catch (_) {
      return `${currency} ${amount.toFixed(fractionDigits(currency))}`;
    }
  };

  const convertAmount = (amount, target) => {
    if (target === base) return amount;
    if (window.Currency?.convert) {
      try { return window.Currency.convert(amount, base, target); } catch (_) {}
    }
    return amount;
  };

  const renderElement = (el, target) => {
    const cents = Number(el.dataset.moneyCents);
    if (!Number.isFinite(cents)) return;
    const converted = convertAmount(cents / 100, target);
    const prefix = el.dataset.moneyPrefix || '';
    const suffix = el.dataset.moneySuffix || '';
    el.textContent = `${prefix}${format(converted, target)}${suffix}`;
    el.dataset.displayCurrency = target;
  };

  const regionEmojiForCurrency = currency => ({EUR:'🇪🇺', USD:'🇺🇸'})[currency] || '';

  const updateLocalizationLabels = target => {
    document.querySelectorAll('[data-reem-current-currency]').forEach(el => {
      el.textContent = target;
    });
    document.querySelectorAll('[data-reem-country-currency]').forEach(el => {
      const country = el.getAttribute('data-country-code');
      const currency = currencyForCountry(country);
      el.textContent = currency;
    });

    const regionalEmoji = regionEmojiForCurrency(target);
    document.querySelectorAll('[data-reem-current-flag]').forEach(el => {
      el.textContent = regionalEmoji;
      el.hidden = !regionalEmoji;
    });
    document.querySelectorAll('[data-reem-shopify-flag]').forEach(el => {
      el.hidden = Boolean(regionalEmoji);
    });
    document.querySelectorAll('[data-reem-region-option]').forEach(el => {
      el.classList.toggle('selected', el.getAttribute('data-reem-region-option') === target);
    });
  };

  const renderAll = (root = document) => {
    const target = getCurrency();
    root.querySelectorAll?.('[data-money-cents]').forEach(el => renderElement(el, target));
    updateLocalizationLabels(target);
    document.documentElement.dataset.displayCurrency = target;
    document.dispatchEvent(new CustomEvent('reem:currency:updated', {detail: {currency: target, country: detectedCountry}}));
  };

  const detectCountryByIp = async () => {
    const endpoints = [
      ['https://ipapi.co/json/', data => data && data.country_code],
      ['https://ipwho.is/', data => data && data.success !== false && data.country_code]
    ];
    for (const [url, pick] of endpoints) {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4500);
        const response = await fetch(url, {cache:'no-store', signal:controller.signal});
        clearTimeout(timer);
        if (!response.ok) continue;
        const data = await response.json();
        const country = String(pick(data) || '').toUpperCase();
        if (country.length === 2) {
          detectedCountry = country;
          autoCurrency = currencyForCountry(country);
          renderAll();
          return;
        }
      } catch (_) {}
    }
  };

  const boot = () => {
    renderAll();
    detectCountryByIp();

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; renderAll(); });
    });
    observer.observe(document.body, {childList: true, subtree: true, characterData: false});

    document.addEventListener('click', event => {
      const regionalButton = event.target.closest?.('[data-reem-currency-choice]');
      if (regionalButton) {
        event.preventDefault();
        event.stopPropagation();
        const currency = String(regionalButton.getAttribute('data-reem-currency-choice') || '').toUpperCase();
        if (supported.has(currency)) {
          try { localStorage.setItem(storageKey, currency); } catch (_) {}
          autoCurrency = currency;
          renderAll();
          const popup = regionalButton.closest('[localization-popup]');
          popup?.querySelector?.('[localization-popup-close]')?.click();
          document.querySelectorAll('[localization-trigger][aria-expanded="true"]').forEach(trigger => trigger.click());
        }
        return;
      }

      const countryButton = event.target.closest?.('button[name="country_code"]');
      if (countryButton) {
        const currency = currencyForCountry(countryButton.value);
        try { localStorage.setItem(storageKey, currency); } catch (_) {}
      }
    }, true);

    document.addEventListener('change', event => {
      const selector = event.target.closest?.('[data-reem-currency-select]');
      if (!selector) return;
      const value = String(selector.value || '').toUpperCase();
      if (!supported.has(value)) return;
      try { localStorage.setItem(storageKey, value); } catch (_) {}
      renderAll();
    });

    window.ReemCurrencyConverter = {
      set(currency) {
        currency = String(currency || '').toUpperCase();
        if (!supported.has(currency)) return false;
        try { localStorage.setItem(storageKey, currency); } catch (_) {}
        renderAll();
        return true;
      },
      clearManual() {
        try { localStorage.removeItem(storageKey); } catch (_) {}
        autoCurrency = currencyForCountry(detectedCountry);
        renderAll();
      },
      get: getCurrency,
      refresh: renderAll
    };
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once:true});
  else boot();
})();
